/**
 * API Service Layer
 * Handles all API requests with retry logic, error handling, and timeouts
 */

import { config } from './config.js';
import { logError } from './utils.js';

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Make HTTP request with timeout
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>}
 */
const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408, null);
    }
    throw error;
  }
};

/**
 * Make API request with retry logic
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>}
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${config.api.baseUrl}${endpoint}`;
  let lastError;

  for (let attempt = 1; attempt <= config.api.retryAttempts; attempt++) {
    try {
      const response = await fetchWithTimeout(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // Handle HTTP errors
      if (!response.ok) {
        const errorText = await response.text();
        throw new ApiError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorText
        );
      }

      // Parse JSON response
      const data = await response.json();
      return data;
    } catch (error) {
      lastError = error;

      // Log error with context
      logError(error, {
        endpoint,
        attempt,
        maxAttempts: config.api.retryAttempts,
      });

      // Don't retry on client errors (4xx) except 408 (timeout) and 429 (rate limit)
      if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500 &&
        error.status !== 408 &&
        error.status !== 429
      ) {
        throw error;
      }

      // Wait before retry (exponential backoff)
      if (attempt < config.api.retryAttempts) {
        const delay = config.api.retryDelay * Math.pow(2, attempt - 1);
        await sleep(delay);
      }
    }
  }

  // All retries failed
  throw lastError;
};

/**
 * API Service object with all endpoints
 */
export const api = {
  /**
   * Search for recommendations/suggestions
   * @param {string} type - Search type ('group' or 'stock_item')
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of suggestions
   */
  async search(type, query) {
    try {
      const params = new URLSearchParams({
        type: type === 'group' ? 'group' : 'stock_item',
        query: query, // URLSearchParams will encode automatically
      });

      const data = await apiRequest(`/search?${params.toString()}`);
      // API returns {results: [...]}
      return Array.isArray(data?.results) ? data.results : [];
    } catch (error) {
      logError(error, { method: 'search', type, query });
      throw new ApiError(
        'Failed to fetch search suggestions. Please try again.',
        error.status,
        error.response
      );
    }
  },

  /**
   * Get scheme list results
   * @param {string} type - Search type ('group' or 'stock_item')
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of schemes
   */
  async getSchemeList(type, query) {
    try {
      const paramKey = type === 'group' ? 'group' : 'item';
      // Manually encode: spaces as %20, + stays as +
      const encodedQuery = encodeURIComponent(query).replace(/%2B/g, '+');

      // Get current date in d-m-yyyy format
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth() + 1; // Month is 0-indexed
      const year = now.getFullYear();
      const todayDate = `${day}-${month}-${year}`;

      const queryString = `${paramKey}=${encodedQuery}&website=y&date=${todayDate}`;

      const data = await apiRequest(`/schemegrouplist?${queryString}`);
      // API returns {SchemeGroupList: [...]} array
      return Array.isArray(data?.SchemeGroupList) ? data.SchemeGroupList : [];
    } catch (error) {
      logError(error, { method: 'getSchemeList', type, query });
      throw new ApiError(
        'Failed to fetch scheme results. Please try again.',
        error.status,
        error.response
      );
    }
  },
};

/**
 * Check API health
 * @returns {Promise<boolean>}
 */
export const checkApiHealth = async () => {
  try {
    await fetchWithTimeout(config.api.baseUrl, { method: 'HEAD' });
    return true;
  } catch (error) {
    logError(error, { method: 'checkApiHealth' });
    return false;
  }
};
